import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const brtDateKey = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
  } catch {
    return "";
  }
};

const DOT = {
  followup: "bg-blue-500",
  visita: "bg-amber-500",
  documento: "bg-purple-500",
  geral: "bg-stone-400",
};
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export const AgendaCalendarView = ({ tasks, selectedDay, onSelectDay }) => {
  const todayKey = brtDateKey(new Date().toISOString());
  const [month, setMonth] = useState(() => {
    const [y, m] = todayKey.split("-").map(Number);
    return { y, m: m - 1 };
  });

  const byDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      const k = brtDateKey(t.remind_at);
      if (!k) return;
      (map[k] = map[k] || []).push(t);
    });
    return map;
  }, [tasks]);

  const cells = useMemo(() => {
    const first = new Date(month.y, month.m, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return { key, day: d.getDate(), inMonth: d.getMonth() === month.m };
    });
  }, [month]);

  const monthLabel = new Date(month.y, month.m, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const nav = (delta) =>
    setMonth(({ y, m }) => {
      const d = new Date(y, m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  const goToday = () => {
    const [y, m] = todayKey.split("-").map(Number);
    setMonth({ y, m: m - 1 });
    onSelectDay(todayKey);
  };

  return (
    <div className="bg-white border rounded-xl p-4" data-testid="agenda-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav(-1)} data-testid="calendar-prev"
          className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"><ChevronLeft className="w-4 h-4" /></button>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-[#1B3A2F] capitalize" data-testid="calendar-month-label">{monthLabel}</span>
          <button onClick={goToday} data-testid="calendar-today-btn"
            className="text-[11px] px-2 py-0.5 rounded-full border border-stone-200 text-stone-500 hover:border-stone-400">Hoje</button>
        </div>
        <button onClick={() => nav(1)} data-testid="calendar-next"
          className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[10px] uppercase tracking-wide text-stone-400 font-semibold py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
        {cells.map((c) => {
          const dayTasks = byDay[c.key] || [];
          const cats = [...new Set(dayTasks.map((t) => t.category || "geral"))].slice(0, 4);
          const isToday = c.key === todayKey;
          const isSelected = c.key === selectedDay;
          return (
            <button key={c.key} onClick={() => onSelectDay(isSelected ? null : c.key)}
              data-testid={`calendar-day-${c.key}`}
              className={`relative min-h-[52px] rounded-lg border p-1 text-left transition-colors
                ${c.inMonth ? "bg-white" : "bg-stone-50"}
                ${isSelected ? "border-[#D4651A] ring-1 ring-[#D4651A]" : isToday ? "border-[#1B3A2F]" : "border-stone-100 hover:border-stone-300"}`}>
              <span className={`text-xs ${c.inMonth ? (isToday ? "font-bold text-[#1B3A2F]" : "text-stone-600") : "text-stone-300"}`}>
                {c.day}
              </span>
              {dayTasks.length > 0 && (
                <div className="absolute bottom-1 left-1 right-1 flex items-center gap-0.5">
                  {cats.map((cat) => (
                    <span key={cat} className={`w-1.5 h-1.5 rounded-full ${DOT[cat] || DOT.geral}`} />
                  ))}
                  <span className="ml-auto text-[10px] text-stone-400 font-medium">{dayTasks.length}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        {Object.entries({ followup: "Follow-up", visita: "Visita", documento: "Documentação", geral: "Geral" }).map(([k, label]) => (
          <span key={k} className="flex items-center gap-1 text-[11px] text-stone-500">
            <span className={`w-2 h-2 rounded-full ${DOT[k]}`} /> {label}
          </span>
        ))}
      </div>
    </div>
  );
};
